<template>
  <div class="content">
    <div class="sd-pathway-builder-wrap">
      <card class="card no-border-card" no-body>
        <div class="pb">

          <!-- ============ TOP HEADER ============ -->
          <header class="pb-top">
            <div class="pb-top__left">
              <nav class="pb-crumbs">
                <a class="pb-crumb" @click="goBack">← Pathways</a>
                <span class="pb-crumb-sep">/</span>
                <span class="pb-crumb pb-crumb--current">{{ isEdit ? 'Edit pathway' : 'New pathway' }}</span>
              </nav>

              <div class="pb-title-row">
                <input
                  v-model="pathway.title"
                  class="pb-title-input"
                  placeholder="Untitled pathway"
                  maxlength="120"
                  aria-label="Pathway title"
                  @input="markDirty" />
                <span class="pb-status" :class="'pb-status--' + (pathway.status || 'draft')">
                  {{ statusLabel(pathway.status) }}
                </span>
              </div>

              <input
                v-model="pathway.subtitle"
                class="pb-subtitle-input"
                placeholder="Add modules and organize them into a meaningful training sequence."
                maxlength="200"
                aria-label="Pathway subtitle"
                @input="markDirty" />

              <div class="pb-meta-row">
                <!-- Who this pathway is for. Required: it decides which new users
                     receive it automatically at sign-up. -->
                <div class="pb-meta-field">
                  <label class="pb-meta-label" for="pb-pathway-classification">
                    Pathway type <span class="pb-meta-required" aria-hidden="true">*</span>
                  </label>
                  <div class="pb-meta-select-wrap">
                    <select
                      id="pb-pathway-classification"
                      v-model="pathway.classification"
                      class="pb-meta-select"
                      :class="{ 'pb-meta-select--missing': !pathway.classification }"
                      required
                      @change="markDirty">
                      <option value="" disabled>Select who it's for…</option>
                      <option v-for="c in classifications" :key="c.value" :value="c.value">{{ c.label }}</option>
                    </select>
                    <svg class="pb-meta-select__chev" viewBox="0 0 20 20" fill="currentColor" width="11" height="11" aria-hidden="true"><path fill-rule="evenodd" d="M5.22 8.22a.75.75 0 011.06 0L10 11.94l3.72-3.72a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 010-1.06z" clip-rule="evenodd"/></svg>
                  </div>
                  <span v-if="!pathway.classification && isEdit" class="pb-meta-hint pb-meta-hint--warn">
                    Not set — this pathway is never assigned automatically until a type is chosen.
                  </span>
                  <span v-else-if="classificationHint" class="pb-meta-hint">{{ classificationHint }}</span>
                </div>
              </div>
            </div>

            <div class="pb-top__stats">
              <div class="pb-stat">
                <div class="pb-stat__value">{{ modules.length }}</div>
                <div class="pb-stat__label">Modules</div>
              </div>
              <div class="pb-stat">
                <div v-if="modules.length === 0" class="pb-stat__value pb-stat__value--muted">—</div>
                <div v-else class="pb-stat__value">{{ totalDuration }}<span class="pb-stat__unit">min</span></div>
                <div class="pb-stat__label">Est. time</div>
              </div>
              <div class="pb-stat">
                <div v-if="modules.length === 0" class="pb-stat__value pb-stat__value--muted">—</div>
                <div v-else class="pb-stat__value">{{ gatedCount }}<span class="pb-stat__unit">/{{ modules.length }}</span></div>
                <div class="pb-stat__label">Sequential</div>
              </div>
            </div>

            <div class="pb-top__actions">
              <span v-if="dirty" class="pb-saved pb-saved--dirty">Unsaved</span>
              <span v-else-if="savedAt" class="pb-saved pb-saved--ok">
                Saved just now
                <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" /></svg>
              </span>

              <button class="pb-btn pb-btn--ghost" :disabled="saving"
                :title="previewNeedsSave ? 'Saves a draft first, then opens the learner view' : 'Open the learner view'"
                @click="openPreview">
                <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true"><path fill-rule="evenodd" d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"/></svg>
                {{ previewNeedsSave ? 'Save draft & preview' : 'Preview learner view' }}
              </button>
              <button class="pb-btn pb-btn--primary" :disabled="saving" @click="saveDraft">
                {{ saving ? 'Saving…' : saveLabel }}
              </button>

              <div class="pb-kebab-wrap" v-click-outside="closeMenu">
                <button class="pb-btn pb-btn--icon" title="More options" @click="menuOpen = !menuOpen">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M10 4a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 4.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 4.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3z"/></svg>
                </button>
                <div v-if="menuOpen" class="pb-menu" role="menu">
                  <button class="pb-menu__item" :disabled="!canPublish" @click="onMenuPublish">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd"/></svg>
                    {{ pathway.status === 'published' ? 'Update & republish' : 'Publish' }}
                  </button>
                  <!-- Only a saved pathway can be duplicated; a blank or not-yet-saved
                       form has nothing on the server to copy from. -->
                  <button class="pb-menu__item" :disabled="!pathway.id" @click="onMenuDuplicate">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M7 3a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V5a2 2 0 00-2-2H7z"/><path d="M3 7a2 2 0 012-2v10h8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/></svg>
                    Duplicate
                  </button>
                  <div class="pb-menu__sep"></div>
                  <button class="pb-menu__item pb-menu__item--danger" :disabled="!pathway.id" @click="onMenuArchive">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M2 5a2 2 0 012-2h12a2 2 0 110 4H4a2 2 0 01-2-2zm0 5a2 2 0 012-2h12a2 2 0 012 2v5a2 2 0 01-2 2H4a2 2 0 01-2-2v-5zm6 1a1 1 0 100 2h4a1 1 0 100-2H8z"/></svg>
                    Archive
                  </button>
                </div>
              </div>
            </div>
          </header>

          <!-- ============ 3-COLUMN BODY ============ -->
          <div class="pb-body">

            <!-- ----- LEFT: Course library ----- -->
            <aside class="pb-pane pb-pane--library">
              <!-- Shown while a canvas module is being dragged: the library is
                   also the "take it out of the pathway" target. Pointer events
                   stay off so it never steals the drop. -->
              <div v-if="canvasDragging" class="pb-remove-hint" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"><path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4z" clip-rule="evenodd"/></svg>
                Drop here to remove from the pathway
              </div>

              <div class="pb-pane__head">
                <h3 class="pb-pane__title">Course library</h3>
                <p class="pb-pane__sub">Drag items to the canvas or click +</p>
              </div>

              <div class="pb-search-row">
                <div class="pb-search">
                  <svg class="pb-search__icon" viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true"><path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd"/></svg>
                  <input
                    ref="librarySearchInput"
                    v-model="librarySearch"
                    class="pb-search__input"
                    aria-label="Search courses"
                    placeholder="Search courses…" />
                </div>
              </div>

              <div class="pb-tabs">
                <button class="pb-tab" :class="{ 'pb-tab--active': libraryTab === 'all' }" @click="libraryTab = 'all'">All</button>
                <button class="pb-tab" :class="{ 'pb-tab--active': libraryTab === 'in' }" @click="libraryTab = 'in'">In pathway</button>
                <button class="pb-tab" :class="{ 'pb-tab--active': libraryTab === 'out' }" @click="libraryTab = 'out'">Not in pathway</button>
              </div>

              <div v-if="loadingCourses" class="pb-pane__loading">
                <span class="pb-spinner"></span>
              </div>

              <!-- Always rendered, even with nothing in it, so a module dragged
                   off the canvas always has somewhere to land. -->
              <ul
                v-else
                ref="libraryList"
                class="pb-lib-list"
                :class="{ 'pb-lib-list--removing': canvasDragging }">
                <li v-if="visibleLibraryCourses.length === 0" key="lib-empty" class="pb-lib-static">
                  {{ canvasDragging ? 'Drop a module here to remove it' : 'No matching courses.' }}
                </li>
                <li
                  v-for="course in visibleLibraryCourses"
                  :key="course.id"
                  class="pb-lib-item"
                  :data-course-id="course.id"
                  :class="{ 'pb-lib-item--used': isUsed(course.id) }"
                  :title="isUsed(course.id) ? 'Already in this pathway' : 'Drag to add to pathway'">
                  <span class="pb-lib-item__grip" aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path d="M7 4a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM7 8.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM7 13a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z"/></svg>
                  </span>
                  <span class="pb-lib-item__icon" aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M4 4a2 2 0 012-2h6.5a.5.5 0 01.354.146l3 3A.5.5 0 0116 5.5V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V6h-3a1 1 0 01-1-1V4H6z"/></svg>
                  </span>
                  <div class="pb-lib-item__body">
                    <div class="pb-lib-item__title">{{ courseLabel(course) }}</div>
                    <div class="pb-lib-item__meta">
                      <span class="pb-lib-item__time">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="10" height="10" aria-hidden="true"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-12.25a.75.75 0 00-1.5 0v5a.75.75 0 00.336.624l3 2a.75.75 0 00.832-1.248L10.75 10.348V5.75z" clip-rule="evenodd" /></svg>
                        {{ courseDuration(course) }} min
                      </span>
                      <span v-if="isUsed(course.id)" class="pb-lib-item__used">In pathway</span>
                    </div>
                  </div>
                  <button
                    class="pb-lib-item__add"
                    type="button"
                    :disabled="isUsed(course.id)"
                    :title="isUsed(course.id) ? 'Already in this pathway' : 'Add to pathway'"
                    @click="addCourse(course)">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12"><path fill-rule="evenodd" d="M10 5a.75.75 0 01.75.75v3.5h3.5a.75.75 0 010 1.5h-3.5v3.5a.75.75 0 01-1.5 0v-3.5h-3.5a.75.75 0 010-1.5h3.5v-3.5A.75.75 0 0110 5z" clip-rule="evenodd" /></svg>
                  </button>
                </li>
              </ul>
            </aside>

            <!-- ----- MIDDLE: Canvas ----- -->
            <main class="pb-pane pb-pane--canvas">
              <div class="pb-canvas__head">
                <div class="pb-canvas__head-left">
                  <h3 class="pb-pane__title">Pathway canvas</h3>
                  <span class="pb-canvas__summary">
                    <strong>{{ modules.length }}</strong> module{{ modules.length === 1 ? '' : 's' }}
                    <template v-if="modules.length > 0">
                      <span class="pb-canvas__summary-sep">·</span>
                      <strong>{{ gatedCount }}</strong> gated
                    </template>
                  </span>
                </div>
                <div class="pb-canvas__head-tools">
                  <button
                    class="pb-icon-btn pb-icon-btn--danger"
                    title="Clear all modules"
                    type="button"
                    :disabled="modules.length === 0"
                    @click="clearAllModules">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4z" clip-rule="evenodd"/></svg>
                  </button>
                </div>
              </div>

              <div class="pb-canvas__body" @click.self="clearSelection">
                <ol
                  ref="canvasList"
                  class="pb-canvas-list"
                  :class="{ 'pb-canvas-list--receiving': libraryDragging }"
                  @click.self="clearSelection">
                  <li
                    v-for="(mod, idx) in modules"
                    :key="'mod-' + (mod.id || mod._tempId)"
                    class="pb-mod-row"
                    :data-module-id="mod.id || mod._tempId">
                    <div
                      class="pb-mod"
                      :class="{ 'pb-mod--selected': isSelected(mod) }"
                      title="Drag anywhere on the tile to reorder, or onto the library to remove"
                      @click="selectModule(mod)">
                      <div class="pb-mod__drag" aria-hidden="true" @click.stop>
                        <svg viewBox="0 0 20 20" width="13" height="13" fill="currentColor"><path d="M7 4a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm0 5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm0 5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6-10a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm0 5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm0 5a1.5 1.5 0 100 3 1.5 1.5 0 000-3z"/></svg>
                      </div>
                      <div class="pb-mod__seq" aria-hidden="true">{{ idx + 1 }}</div>
                      <div class="pb-mod__main">
                        <div class="pb-mod__title">{{ mod.course_name || courseTitle(mod.course_id) }}</div>
                        <div class="pb-mod__meta">
                          <span class="pb-mod__time">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="10" height="10" aria-hidden="true"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-12.25a.75.75 0 00-1.5 0v5a.75.75 0 00.336.624l3 2a.75.75 0 00.832-1.248L10.75 10.348V5.75z" clip-rule="evenodd" /></svg>
                            {{ moduleDuration(mod) }} min
                          </span>
                          <span class="pb-mod__chip" :class="'pb-mod__chip--' + chipKind(mod)">
                            {{ chipLabel(mod) }}
                          </span>
                        </div>
                      </div>
                      <select
                        v-model="mod.gating_rule"
                        class="pb-rule-select"
                        aria-label="Unlock rule"
                        @click.stop
                        @change="onModuleChange(mod)">
                        <option value="optional">Always unlocked</option>
                        <option value="complete_previous">Unlock after previous</option>
                        <option value="unlock_on_date">Unlock on date</option>
                      </select>
                      <button
                        class="pb-mod__remove"
                        type="button"
                        title="Remove module"
                        @click.stop="removeModule(mod, idx)">
                        <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor"><path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4z" clip-rule="evenodd" /></svg>
                      </button>
                    </div>

                    <div class="pb-flow-sep">
                      <span class="pb-flow-line"></span>
                      <button class="pb-flow-plus" type="button" title="Insert module here" @click="focusLibrarySearch">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M10 5a.75.75 0 01.75.75v3.5h3.5a.75.75 0 010 1.5h-3.5v3.5a.75.75 0 01-1.5 0v-3.5h-3.5a.75.75 0 010-1.5h3.5v-3.5A.75.75 0 0110 5z" clip-rule="evenodd" /></svg>
                      </button>
                      <span class="pb-flow-line"></span>
                    </div>
                  </li>

                  <!-- Empty state: a non-draggable row inside the list, so the
                       list keeps its height and a drop anywhere on it lands. -->
                  <li v-if="modules.length === 0" key="empty" class="pb-canvas-static pb-empty-state" @click="clearSelection">
                    <div class="pb-empty-state__art" aria-hidden="true">
                      <svg viewBox="0 0 220 150" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="18" y="22" width="148" height="98" rx="7" fill="#FFFFFF" stroke="#CBD5E1" stroke-width="1.4"/>
                      <circle cx="28" cy="32" r="1.8" fill="#CBD5E1"/>
                      <circle cx="34" cy="32" r="1.8" fill="#CBD5E1"/>
                      <circle cx="40" cy="32" r="1.8" fill="#CBD5E1"/>
                      <line x1="18" y1="42" x2="166" y2="42" stroke="#E2E8F0" stroke-width="1"/>
                      <rect x="32" y="56" width="50" height="48" rx="5" stroke="#94A3B8" stroke-width="1.4" stroke-dasharray="3 3" fill="#F8FAFC"/>
                      <line x1="57" y1="72" x2="57" y2="88" stroke="#3E7799" stroke-width="1.6" stroke-linecap="round"/>
                      <line x1="49" y1="80" x2="65" y2="80" stroke="#3E7799" stroke-width="1.6" stroke-linecap="round"/>
                      <rect x="92" y="56" width="58" height="48" rx="5" fill="#FFFFFF" stroke="#CBD5E1" stroke-width="1.4"/>
                      <rect x="100" y="63" width="20" height="14" rx="2" fill="#1E3A5F"/>
                      <path d="M107 66.5L107 73.5L113 70Z" fill="#FFFFFF"/>
                      <line x1="100" y1="84" x2="142" y2="84" stroke="#E2E8F0" stroke-width="1.4" stroke-linecap="round"/>
                      <line x1="100" y1="91" x2="130" y2="91" stroke="#E2E8F0" stroke-width="1.4" stroke-linecap="round"/>
                      <path d="M174 26 L175.3 29.1 L178.4 30.4 L175.3 31.7 L174 34.8 L172.7 31.7 L169.6 30.4 L172.7 29.1 Z" fill="#94A3B8"/>
                      <path d="M183 42 L183.7 43.7 L185.4 44.4 L183.7 45.1 L183 46.8 L182.3 45.1 L180.6 44.4 L182.3 43.7 Z" fill="#94A3B8"/>
                      <path d="M178 96 L178 120 L184 114 L188.5 124 L192 122 L187.5 112 L194.5 112 Z" fill="#1E3A5F" stroke="#FFFFFF" stroke-width="0.8" stroke-linejoin="round"/>
                    </svg>
                    </div>
                    <div class="pb-empty-state__title">{{ libraryDragging ? 'Drop the course here' : 'Your pathway is empty' }}</div>
                    <div class="pb-empty-state__sub">Drag courses here from the library to build your pathway, or click + on a course.</div>
                  </li>

                  <!-- Dropzone under the modules: a hint that the end of the
                       list accepts drops too. -->
                  <li v-else key="dropzone" class="pb-canvas-static pb-dropzone" @click="clearSelection">
                    <div class="pb-dropzone__plus" aria-hidden="true">
                      <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M10 5a.75.75 0 01.75.75v3.5h3.5a.75.75 0 010 1.5h-3.5v3.5a.75.75 0 01-1.5 0v-3.5h-3.5a.75.75 0 010-1.5h3.5v-3.5A.75.75 0 0110 5z" clip-rule="evenodd" /></svg>
                    </div>
                    <div class="pb-dropzone__title">{{ libraryDragging ? 'Drop to add at the end' : 'Add another module' }}</div>
                    <div class="pb-dropzone__sub">Drag a course from the library, or click + on it</div>
                  </li>
                </ol>
              </div>
            </main>

            <!-- ----- RIGHT: Inspector ----- -->
            <aside class="pb-pane pb-pane--inspector">
              <div class="pb-pane__head pb-pane__head--insp">
                <h3 class="pb-pane__title">Module settings</h3>
                <button
                  v-if="selectedModule"
                  class="pb-insp-close"
                  type="button"
                  title="Close"
                  @click="selectedModuleKey = null">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M4.28 3.22a.75.75 0 00-1.06 1.06L8.94 10l-5.72 5.72a.75.75 0 101.06 1.06L10 11.06l5.72 5.72a.75.75 0 101.06-1.06L11.06 10l5.72-5.72a.75.75 0 00-1.06-1.06L10 8.94 4.28 3.22z" clip-rule="evenodd"/></svg>
                </button>
              </div>

              <div v-if="selectedModule" class="pb-insp">
                <div class="pb-insp__scroll">
                  <div class="pb-insp__section">
                    <div class="pb-insp__course">{{ selectedModule.course_name || courseTitle(selectedModule.course_id) }}</div>
                    <div class="pb-insp__course-sub">Step {{ selectedIndex + 1 }} of {{ modules.length }}</div>
                  </div>

                  <div class="pb-insp__section">
                    <div class="pb-insp__section-title">Unlock rules</div>
                    <div class="pb-insp__section-sub">Decide when this module becomes available.</div>
                    <div class="pb-radio-group">
                      <label class="pb-radio">
                        <input type="radio" value="optional" v-model="selectedModule.gating_rule" @change="onModuleChange(selectedModule)" />
                        <span class="pb-radio__body">
                          <span class="pb-radio__title">Always unlocked</span>
                          <span class="pb-radio__sub">Available as soon as the user enrolls</span>
                        </span>
                      </label>
                      <label class="pb-radio">
                        <input type="radio" value="complete_previous" v-model="selectedModule.gating_rule" @change="onModuleChange(selectedModule)" />
                        <span class="pb-radio__body">
                          <span class="pb-radio__title">Unlock after previous</span>
                          <span class="pb-radio__sub">The previous module must be completed first</span>
                        </span>
                      </label>
                      <label class="pb-radio">
                        <input type="radio" value="unlock_on_date" v-model="selectedModule.gating_rule" @change="onModuleChange(selectedModule)" />
                        <span class="pb-radio__body">
                          <span class="pb-radio__title">Unlock on date</span>
                          <span class="pb-radio__sub">Set a specific date and time</span>
                        </span>
                      </label>
                    </div>

                    <div v-if="selectedModule.gating_rule === 'unlock_on_date'" class="pb-date-field">
                      <label class="pb-field-label" :for="'pb-unlock-at-' + (selectedModule.id || selectedModule._tempId)">
                        Unlocks on
                      </label>
                      <input
                        :id="'pb-unlock-at-' + (selectedModule.id || selectedModule._tempId)"
                        type="datetime-local"
                        class="pb-date-input"
                        :class="{ 'pb-date-input--missing': !selectedModule.unlock_at }"
                        v-model="selectedModule.unlock_at"
                        @input="onModuleChange(selectedModule)" />
                      <span v-if="!selectedModule.unlock_at" class="pb-field-hint pb-field-hint--warn">
                        Required — the pathway can't be saved until a date is set.
                      </span>
                      <span v-else class="pb-field-hint">Shown to learners in their own time zone.</span>
                    </div>
                  </div>
                </div>

                <div class="pb-insp__footer">
                  <button class="pb-btn pb-btn--destructive pb-btn--block" type="button" @click="removeSelected">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4z" clip-rule="evenodd" /></svg>
                    Remove from pathway
                  </button>
                </div>
              </div>

              <div v-else class="pb-insp-empty">
                <div class="pb-insp-empty__title">Select a module</div>
                <div class="pb-insp-empty__sub">Choose a module on the canvas to view and edit its settings.</div>
              </div>
            </aside>
          </div>

          <div v-if="toast" class="pb-toast" :class="'pb-toast--' + toast.kind">{{ toast.msg }}</div>

          <!-- Asked before the editor is left with work that is not saved. -->
          <div v-if="leaveGuard.show" class="pb-leave" role="dialog" aria-modal="true" aria-labelledby="pb-leave-title">
            <div class="pb-leave__box">
              <h3 id="pb-leave-title" class="pb-leave__title">Unsaved changes</h3>
              <p class="pb-leave__text">
                Your current changes have not been saved. Please save your work before leaving this page.
              </p>
              <div class="pb-leave__actions">
                <button ref="leaveStay" type="button" class="pb-btn pb-btn--primary" @click="continueEditing">
                  Continue Editing
                </button>
                <button type="button" class="pb-btn pb-btn--destructive" @click="leaveWithoutSaving">
                  Leave Without Saving
                </button>
              </div>
            </div>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import Sortable from 'sortablejs';
import {
  getSafeDigTemplate,
  SAFEDIG_PATHWAY_CLASSIFICATIONS,
} from '@/utils/safeDigPathwayTemplates';

// Locally registered click-outside directive — closes the kebab menu.
const clickOutside = {
  bind(el, binding) {
    el.__pbClickOutside = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event);
      }
    };
    document.addEventListener('click', el.__pbClickOutside);
  },
  unbind(el) {
    document.removeEventListener('click', el.__pbClickOutside);
    delete el.__pbClickOutside;
  },
};

/** Fallback minutes when a course carries no length. */
const DEFAULT_MODULE_MINUTES = 12;

export default {
  name: 'PathwayBuilder',
  directives: { clickOutside },
  data() {
    return {
      pathway: {
        id: null,
        title: '',
        subtitle: '',
        description: '',
        thumbnail: '',
        classification: '',
        status: 'draft',
      },
      modules: [],
      courses: [],
      classifications: SAFEDIG_PATHWAY_CLASSIFICATIONS,
      loadingCourses: false,
      librarySearch: '',
      libraryTab: 'all',
      saving: false,
      dirty: false,
      savedAt: null,
      toast: null,
      sortableLibrary: null,
      sortableCanvas: null,
      // A library course is mid-drag: the canvas highlights as the target.
      libraryDragging: false,
      // A canvas module is mid-drag: the library highlights as the remove target.
      canvasDragging: false,
      tempIdCounter: 1,
      selectedModuleKey: null,
      menuOpen: false,
      // Modules already saved that have been taken off the canvas. They are
      // deleted when the pathway is saved, not before, so leaving without
      // saving leaves the pathway as it was.
      removedModuleIds: [],
      // Set while the editor is asking whether to leave unsaved work behind.
      // `proceed` is vue-router's next(), which must be called exactly once.
      leaveGuard: { show: false, proceed: null },
      // One navigation that has already asked its own question.
      skipLeaveGuardOnce: false,
    };
  },
  computed: {
    isEdit() { return !!this.$route.params.id; },
    /**
     * A published pathway is edited in place, so the primary button is "Save
     * changes" — it keeps the pathway live. Calling it "Save draft" there
     * read as, and used to be, a demotion that emptied every learner's
     * dashboard.
     */
    saveLabel() {
      return this.pathway.status === 'published' ? 'Save changes' : 'Save draft';
    },
    /** The learner view reads from the server, so unsaved work would be missing from it. */
    previewNeedsSave() {
      return !this.pathway.id || this.dirty;
    },
    canPublish() {
      return !!(this.pathway.title && this.pathway.title.trim())
        && this.modules.length > 0
        && !!this.pathway.classification;
    },
    classificationHint() {
      const c = this.classifications.find(x => x.value === this.pathway.classification);
      return c ? c.hint : '';
    },
    gatedCount() {
      return this.modules.filter(m => m.gating_rule && m.gating_rule !== 'optional').length;
    },
    totalDuration() {
      return this.modules.reduce((sum, m) => sum + this.moduleDuration(m), 0);
    },
    filteredCourses() {
      const q = (this.librarySearch || '').trim().toLowerCase();
      if (!q) return this.courses;
      return this.courses.filter(c => this.courseLabel(c).toLowerCase().indexOf(q) !== -1);
    },
    visibleLibraryCourses() {
      let list = this.filteredCourses;
      if (this.libraryTab === 'in') list = list.filter(c => this.isUsed(c.id));
      else if (this.libraryTab === 'out') list = list.filter(c => !this.isUsed(c.id));
      return list;
    },
    selectedModule() {
      if (this.selectedModuleKey == null) return null;
      return this.modules.find(m => (m.id || m._tempId) === this.selectedModuleKey) || null;
    },
    selectedIndex() {
      if (!this.selectedModule) return -1;
      return this.modules.indexOf(this.selectedModule);
    },
  },
  created() {
    this.fetchCourses();
    this.initFromRoute();
  },
  mounted() {
    this.$nextTick(() => this.setupSortables());
    // Refreshing, closing the tab or following a link out of the app is the
    // browser's to ask about; it shows its own wording, which we cannot set.
    window.addEventListener('beforeunload', this.warnOnUnload);
  },
  beforeDestroy() {
    if (this.sortableLibrary) this.sortableLibrary.destroy();
    if (this.sortableCanvas) this.sortableCanvas.destroy();
    window.removeEventListener('beforeunload', this.warnOnUnload);
    clearTimeout(this._toastTimer);
  },
  /**
   * Leaving the editor within the app — the back link, the sidebar, the
   * browser's Back button. Unsaved work is asked about first; saved work, or
   * a navigation that has already asked, goes straight through.
   */
  beforeRouteLeave(to, from, next) {
    if (this.skipLeaveGuardOnce || !this.dirty) {
      this.skipLeaveGuardOnce = false;
      next();
      return;
    }
    this.leaveGuard = { show: true, proceed: next };
    this.$nextTick(() => {
      if (this.$refs.leaveStay) this.$refs.leaveStay.focus();
    });
  },
  watch: {
    'modules.length'() { this.$nextTick(() => this.setupCanvasSortable()); },
    visibleLibraryCourses() { this.$nextTick(() => this.setupLibrarySortable()); },
    // /new and /:id/edit render this same component, so vue-router reuses the
    // instance when moving between them (Edit → Duplicate) and created() does
    // not run again. Re-initialise from the route instead.
    $route() { this.initFromRoute(); },
  },
  methods: {
    // ---- Sortable setup ----
    setupSortables() {
      this.setupLibrarySortable();
      this.setupCanvasSortable();
    },
    setupLibrarySortable() {
      const el = this.$refs.libraryList;
      if (!el) return;
      if (this.sortableLibrary) {
        this.sortableLibrary.destroy();
        this.sortableLibrary = null;
      }
      this.sortableLibrary = Sortable.create(el, {
        group: {
          name: 'pathway-modules',
          pull: 'clone',
          // Only a canvas module may be dropped here, and dropping it means
          // "remove it from the pathway" (see onAdd). Library rows never
          // rearrange among themselves.
          put: (to, from, dragEl) => !!dragEl && dragEl.classList.contains('pb-mod-row'),
        },
        sort: false,
        animation: 180,
        // Only real course rows drag: the "no matching courses" / drop-here row
        // is inert, and the + button stays a plain click.
        draggable: '.pb-lib-item',
        filter: '.pb-lib-item__add',
        preventOnFilter: false,
        // On touch a short press starts the drag, so a finger can still scroll
        // the list; a mouse drag starts immediately.
        delay: 160,
        delayOnTouchOnly: true,
        touchStartThreshold: 4,
        ghostClass: 'pb-sortable-ghost',
        chosenClass: 'pb-sortable-chosen',
        dragClass: 'pb-sortable-drag',
        onStart: () => { this.libraryDragging = true; },
        onAdd: (evt) => { this.onModuleDroppedOnLibrary(evt); },
        onEnd: (evt) => {
          this.libraryDragging = false;
          if (evt.to === evt.from) return;
          const courseId = parseInt(evt.item.getAttribute('data-course-id'), 10);
          // Where the course landed among the module rows. Read it off the DOM
          // while the node is still in the canvas: Sortable's newDraggableIndex
          // counts the SOURCE list's draggable selector (.pb-lib-item), which
          // no canvas row matches, so it always reports 0.
          const at = this.dropIndexInCanvas(evt.item);
          // With pull:'clone' Sortable MOVES the original <li> (evt.item, the
          // node Vue rendered and bound the + click to) into the canvas and
          // leaves a bare cloneNode (evt.clone, no listeners) in the library.
          // Put the original back where the clone is and discard the clone,
          // so the library stays the DOM Vue owns; then let the data change
          // re-render the canvas.
          if (evt.clone && evt.clone.parentNode) {
            evt.clone.parentNode.insertBefore(evt.item, evt.clone);
            evt.clone.parentNode.removeChild(evt.clone);
          } else if (evt.item && evt.item.parentNode) {
            evt.item.parentNode.removeChild(evt.item);
          }
          if (!courseId) return;
          if (this.isUsed(courseId)) {
            this.flash('Course already in pathway', 'warn');
            return;
          }
          const course = this.courses.find(c => c.id === courseId);
          if (course) this.addCourseAt(course, at);
        },
      });
    },
    /**
     * Put the canvas list back the way Vue renders it: the module rows in
     * order, then the inert row. A drag that changes nothing leaves no data
     * for Vue to re-render from, so anything Sortable moved stays moved;
     * this walks it back. Every node is one Vue already has in this list, in
     * the order Vue holds them, so its next patch still lines up.
     */
    tidyCanvasOrder() {
      const list = this.$refs.canvasList;
      if (!list) return;

      const rows = [];
      const inert = [];
      for (const child of Array.from(list.children)) {
        if (child.classList.contains('pb-mod-row')) rows.push(child);
        else if (child.classList.contains('pb-canvas-static')) inert.push(child);
      }

      const wanted = this.modules
        .map(m => rows.find(row => row.getAttribute('data-module-id') === String(m.id || m._tempId)))
        .filter(Boolean)
        .concat(inert);

      wanted.forEach((node, i) => {
        if (list.children[i] !== node) list.insertBefore(node, list.children[i] || null);
      });
    },
    /**
     * How many module rows sit before `node` in the canvas list — i.e. the
     * index a course dropped there should take. Counting rows rather than
     * child nodes keeps the empty-state / dropzone rows from shifting it.
     */
    dropIndexInCanvas(node) {
      const list = this.$refs.canvasList;
      if (!node || !list || node.parentNode !== list) return this.modules.length;
      let n = 0;
      for (let el = node.previousElementSibling; el; el = el.previousElementSibling) {
        if (el.classList && el.classList.contains('pb-mod-row')) n += 1;
      }
      return n;
    },
    setupCanvasSortable() {
      const el = this.$refs.canvasList;
      if (!el) return;
      if (this.sortableCanvas) {
        this.sortableCanvas.destroy();
        this.sortableCanvas = null;
      }
      this.sortableCanvas = Sortable.create(el, {
        group: { name: 'pathway-modules', put: true },
        animation: 200,
        // Only module rows sort; the empty-state / dropzone rows are inert.
        draggable: '.pb-mod-row',
        // No `handle`: the whole tile is the drag source. The controls sitting
        // on it keep their own click instead of starting a drag, and
        // preventOnFilter stays off so they still behave natively.
        filter: '.pb-mod__remove, .pb-rule-select, .pb-flow-plus',
        preventOnFilter: false,
        delay: 160,
        delayOnTouchOnly: true,
        touchStartThreshold: 4,
        ghostClass: 'pb-sortable-ghost',
        chosenClass: 'pb-sortable-chosen',
        dragClass: 'pb-sortable-drag',
        onStart: () => { this.canvasDragging = true; },
        // The empty-state / dropzone row sits at the foot of the list and is
        // not draggable, but Sortable will still park a module after it when
        // the pointer goes past it — and if the module lands back on the index
        // it started from, no data changes, nothing re-renders, and the row is
        // left stranded above it.
        onMove: (evt) => {
          if (evt.from === evt.to && evt.related
              && evt.related.classList.contains('pb-canvas-static')) {
            return false;
          }
          return true;
        },
        onEnd: () => {
          this.canvasDragging = false;
          this.$nextTick(() => this.tidyCanvasOrder());
        },
        onUpdate: (evt) => {
          const from = typeof evt.oldDraggableIndex === 'number' ? evt.oldDraggableIndex : evt.oldIndex;
          const to = typeof evt.newDraggableIndex === 'number' ? evt.newDraggableIndex : evt.newIndex;
          const moved = this.modules.splice(from, 1)[0];
          this.modules.splice(to, 0, moved);
          this.resequence();
          this.selectedModuleKey = moved.id || moved._tempId;
          this.markDirty();
        },
      });
    },
    /**
     * A module was dragged off the canvas and dropped on the course library:
     * take it out of the pathway. Sortable has physically moved the canvas's
     * <li> into the library <ul>; drop that node first so the library stays
     * the DOM Vue owns, then change the data and let both lists re-render.
     *
     * Only a real drop gets here - an invalid drop or a cancelled drag never
     * fires onAdd, so the pathway is left alone.
     */
    onModuleDroppedOnLibrary(evt) {
      // Sortable has physically moved the canvas's own <li> into the library
      // list, but Vue still has that node in the canvas's render tree. Patching
      // a list whose DOM moved out from under it throws ("insertBefore ... is
      // not a child of this node") and leaves the canvas stuck, so put the node
      // back exactly where it came from first and let the data change take it
      // out the normal way.
      const node = evt.item;
      if (node && evt.from) {
        evt.from.insertBefore(node, evt.from.children[evt.oldIndex] || null);
      } else if (node && node.parentNode) {
        node.parentNode.removeChild(node);
      }
      this.takeModuleOffCanvas(evt);
    },
    takeModuleOffCanvas(evt) {
      const node = evt.item;
      const key = node ? node.getAttribute('data-module-id') : null;
      let idx = this.modules.findIndex(m => String(m.id || m._tempId) === String(key));
      if (idx < 0) {
        idx = typeof evt.oldDraggableIndex === 'number' ? evt.oldDraggableIndex : -1;
      }
      const mod = idx >= 0 ? this.modules[idx] : null;
      if (!mod) return;

      const label = mod.course_name || this.courseTitle(mod.course_id);
      this.removeModule(mod, idx);
      this.flash('Removed "' + label + '" from the pathway', 'ok');
    },

    // ---- Initialisation ----
    /**
     * Decide what this form shows from the URL alone, so it behaves the same
     * whether the component was just created or reused across a navigation:
     *   /:id/edit          → load that pathway (skipped if it's already loaded,
     *                        which is the case right after a first save).
     *   /new?duplicate=id  → a NEW form pre-filled from pathway `id`.
     *   /new?template=id   → a NEW form pre-filled from a template.
     *   /new               → a blank form.
     */
    initFromRoute() {
      const editId = this.$route.params.id;
      if (editId) {
        if (String(this.pathway.id) !== String(editId)) this.fetchPathway(editId);
        return;
      }

      this.resetForm();
      const source = this.$route.query && this.$route.query.duplicate;
      if (source) {
        this.loadDuplicate(source);
        return;
      }
      this.applyTemplateFromQuery();
    },
    resetForm() {
      this.pathway = {
        id: null,
        title: '',
        subtitle: '',
        description: '',
        thumbnail: '',
        classification: '',
        status: 'draft',
      };
      this.modules = [];
      this.removedModuleIds = [];
      this.selectedModuleKey = null;
      this.dirty = false;
      this.savedAt = null;
    },
    /**
     * Pre-fill a new, unsaved form from an existing pathway. The GET is
     * read-only; the copy is built from fresh objects with no pathway id and
     * no module ids, so isEdit stays false and saving goes through the normal
     * create path. Nothing is written until the user saves.
     */
    loadDuplicate(sourceId) {
      this.$http.get('safedig/pathways/' + sourceId)
        .then(resp => {
          const src = resp.data || {};
          this.pathway = {
            id: null,
            title: src.title ? src.title + ' (Copy)' : '',
            subtitle: src.subtitle || '',
            description: src.description || '',
            thumbnail: src.thumbnail || '',
            classification: src.classification || '',
            status: 'draft',
          };
          this.modules = (src.modules || [])
            .slice()
            .sort((a, b) => a.sequence_order - b.sequence_order)
            .map((m, i) => ({
              _tempId: 't' + (this.tempIdCounter++),
              course_id: m.course_id,
              course_name: m.course_name,
              sequence_order: i + 1,
              gating_rule: m.gating_rule || 'complete_previous',
              unlock_at: this.toLocalInput(m.unlock_at),
            }));
          this.removedModuleIds = [];
          this.dirty = true;
          this.flash('Copied from "' + (src.title || 'pathway') + '" — review and save', 'ok');
          this.$nextTick(() => this.setupCanvasSortable());
        })
        .catch(() => { this.flash('Could not load the pathway to duplicate', 'error'); });
    },

    // ---- Data fetching ----
    fetchCourses() {
      this.loadingCourses = true;
      this.$http.get('safedig/pathways-course-options')
        .then(resp => { this.courses = Array.isArray(resp.data) ? resp.data : []; })
        .catch(() => { this.courses = []; })
        .finally(() => {
          this.loadingCourses = false;
          this.$nextTick(() => this.setupLibrarySortable());
        });
    },
    fetchPathway(id) {
      this.$http.get('safedig/pathways/' + id)
        .then(resp => {
          const p = resp.data || {};
          this.pathway = {
            id: p.id,
            title: p.title || '',
            subtitle: p.subtitle || '',
            description: p.description || '',
            thumbnail: p.thumbnail || '',
            classification: p.classification || '',
            status: p.status || 'draft',
          };
          this.modules = (p.modules || []).map(m => ({
            id: m.id,
            course_id: m.course_id,
            course_name: m.course_name,
            sequence_order: m.sequence_order,
            gating_rule: m.gating_rule || 'complete_previous',
            unlock_at: this.toLocalInput(m.unlock_at),
          }));
          this.removedModuleIds = [];
          this.dirty = false;
          this.$nextTick(() => this.setupCanvasSortable());
        })
        .catch(() => { this.flash('Could not load pathway', 'error'); });
    },
    applyTemplateFromQuery() {
      const id = this.$route.query && this.$route.query.template;
      if (!id) return;
      const tpl = getSafeDigTemplate(id);
      if (!tpl || tpl.id === 'blank') return;
      this.pathway.title = tpl.title || '';
      this.pathway.subtitle = tpl.subtitle || '';
      this.pathway.classification = tpl.classification || '';
      this.dirty = true;
      this.flash('Template applied — review and save', 'ok');
    },

    // ---- Course helpers ----
    courseLabel(course) {
      return course.name || course.title || ('Course #' + course.id);
    },
    courseDuration(course) {
      const n = parseInt(course.length, 10);
      return Number.isFinite(n) && n > 0 ? n : DEFAULT_MODULE_MINUTES;
    },
    isUsed(courseId) {
      return this.modules.some(m => m.course_id === courseId);
    },
    courseTitle(courseId) {
      const c = this.courses.find(x => x.id === courseId);
      return c ? this.courseLabel(c) : ('Course #' + courseId);
    },
    moduleDuration(mod) {
      const c = this.courses.find(x => x.id === mod.course_id);
      return c ? this.courseDuration(c) : DEFAULT_MODULE_MINUTES;
    },
    chipKind(mod) {
      if (mod.gating_rule === 'optional') return 'optional';
      if (mod.gating_rule === 'unlock_on_date') return 'date';
      return 'sequential';
    },
    chipLabel(mod) {
      if (mod.gating_rule === 'optional') return 'Optional';
      if (mod.gating_rule === 'unlock_on_date') return mod.unlock_at ? 'On ' + this.shortDate(mod.unlock_at) : 'On date';
      return 'Sequential';
    },
    statusLabel(s) {
      if (s === 'published') return 'Published';
      if (s === 'archived') return 'Archived';
      return 'Draft';
    },
    /**
     * The API stores unlock_at as ISO-8601 (UTC). <input type="datetime-local">
     * wants "YYYY-MM-DDTHH:mm" in the browser's own zone, and gives the same
     * back — so convert on the way in and out.
     */
    toLocalInput(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      const pad = n => String(n).padStart(2, '0');
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
        + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes());
    },
    toIso(local) {
      if (!local) return null;
      const d = new Date(local);
      return Number.isNaN(d.getTime()) ? null : d.toISOString();
    },
    shortDate(local) {
      const d = new Date(local);
      if (Number.isNaN(d.getTime())) return 'date';
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    /** The gating_config the API expects for a module, or null when unused. */
    gatingConfig(mod) {
      if (mod.gating_rule !== 'unlock_on_date') return null;
      return { unlock_at: this.toIso(mod.unlock_at) };
    },

    // ---- Module mutations ----
    resequence() {
      this.modules.forEach((m, i) => { m.sequence_order = i + 1; });
    },
    makeModule(course) {
      return {
        _tempId: 't' + (this.tempIdCounter++),
        course_id: course.id,
        course_name: this.courseLabel(course),
        sequence_order: this.modules.length + 1,
        gating_rule: 'complete_previous',
        unlock_at: '',
      };
    },
    addCourse(course) {
      if (this.isUsed(course.id)) {
        this.flash('Course already in pathway', 'warn');
        return;
      }
      const mod = this.makeModule(course);
      this.modules.push(mod);
      this.resequence();
      this.selectedModuleKey = mod._tempId;
      this.markDirty();
    },
    addCourseAt(course, index) {
      if (this.isUsed(course.id)) return;
      const mod = this.makeModule(course);
      const at = typeof index === 'number' ? index : this.modules.length;
      this.modules.splice(at, 0, mod);
      this.resequence();
      this.selectedModuleKey = mod._tempId;
      this.markDirty();
    },
    /**
     * Take a module off the canvas. Nothing is written yet: a module that is
     * already saved is noted so the next save deletes it, which is what lets
     * Leave Without Saving leave the pathway as it was.
     */
    removeModule(mod, idx) {
      const wasSelected = this.isSelected(mod);
      this.modules.splice(idx, 1);
      this.resequence();
      if (wasSelected) this.selectedModuleKey = null;
      if (mod.id && this.removedModuleIds.indexOf(mod.id) === -1) {
        this.removedModuleIds.push(mod.id);
      }
      this.markDirty();
    },
    removeSelected() {
      const m = this.selectedModule;
      if (!m) return;
      const idx = this.modules.indexOf(m);
      if (idx < 0) return;
      this.removeModule(m, idx);
    },
    clearAllModules() {
      if (this.modules.length === 0) return;
      if (!window.confirm('Remove all modules from this pathway?')) return;
      // Walk backwards so the indexes stay valid as entries are spliced out.
      for (let i = this.modules.length - 1; i >= 0; i--) {
        this.removeModule(this.modules[i], i);
      }
      this.selectedModuleKey = null;
    },
    onModuleChange() { this.markDirty(); },

    // ---- Selection ----
    isSelected(mod) { return this.selectedModuleKey === (mod.id || mod._tempId); },
    selectModule(mod) { this.selectedModuleKey = mod.id || mod._tempId; },
    /** Clicking the canvas background, off any tile, deselects. */
    clearSelection() { this.selectedModuleKey = null; },

    // ---- Header actions ----
    focusLibrarySearch() {
      const el = this.$refs.librarySearchInput;
      if (el) el.focus();
    },
    /**
     * The learner view is rendered from what the API has, so a new pathway
     * or unsaved edits would preview as "no modules". Save a draft first
     * (same validation and toasts as Save draft), then open it.
     */
    async openPreview() {
      if (this.saving) return;
      if (this.previewNeedsSave) {
        const saved = await this.persist({ publish: false });
        if (!saved) return; // persist() already said what is missing
      }
      this.$router.push('/safedig/pathways/' + this.pathway.id);
    },
    onMenuPublish() {
      this.menuOpen = false;
      if (!this.canPublish) {
        this.flash('Add a title, a pathway type and at least one module first', 'warn');
        return;
      }
      this.publish();
    },
    onMenuDuplicate() {
      this.menuOpen = false;
      if (!this.pathway.id) return;
      // The copy is taken from what the server has, so unsaved edits here
      // would not be in it.
      if (this.dirty && !window.confirm(
        'You have unsaved changes. The duplicate is made from the last saved version of this pathway. Continue?'
      )) return;
      // Asked and answered — do not ask again on the way out.
      this.skipLeaveGuardOnce = true;
      this.$router.push({ path: '/safedig/pathways/new', query: { duplicate: String(this.pathway.id) } });
    },
    onMenuArchive() {
      this.menuOpen = false;
      if (!this.pathway.id) return;
      if (!window.confirm('Archive this pathway? It will no longer be visible to learners.')) return;
      this.$http.delete('safedig/pathways/' + this.pathway.id)
        .then(() => {
          this.pathway.status = 'archived';
          this.flash('Pathway archived', 'ok');
        })
        .catch(() => this.flash('Could not archive this pathway', 'error'));
    },
    closeMenu() { this.menuOpen = false; },

    // ---- Leaving with unsaved work ----
    /** Stay put, with everything on the page as it was. */
    continueEditing() {
      const proceed = this.leaveGuard.proceed;
      this.leaveGuard = { show: false, proceed: null };
      if (proceed) proceed(false);
    },
    /** Go, and leave the unsaved edits behind. */
    leaveWithoutSaving() {
      const proceed = this.leaveGuard.proceed;
      this.dirty = false;
      this.leaveGuard = { show: false, proceed: null };
      if (proceed) proceed();
    },
    /**
     * The browser's own unsaved-changes prompt. Returning a string is what
     * asks for it; every browser shows its own wording instead of ours.
     */
    warnOnUnload(event) {
      if (!this.dirty) return undefined;
      event.preventDefault();
      event.returnValue = '';
      return '';
    },

    // ---- State helpers ----
    markDirty() {
      this.dirty = true;
      this.savedAt = null;
    },

    // ---- Persistence ----
    saveDraft() { return this.persist({ publish: false }); },
    publish() { return this.persist({ publish: true }); },
    /** Resolves true when the pathway and its modules were written. */
    async persist({ publish }) {
      if (this.saving) return false;
      if (!this.pathway.title || !this.pathway.title.trim()) {
        this.flash('Title is required', 'warn');
        return false;
      }
      // Required for a new pathway, and for publishing an old untyped one —
      // without it the pathway would publish but never reach anyone
      // automatically. A draft edit of a legacy pathway may stay untyped.
      if (!this.pathway.classification && (!this.pathway.id || publish)) {
        this.flash('Choose a pathway type (Homeowner, Professional or Both) first', 'warn');
        return false;
      }
      const undated = this.modules.find(m => m.gating_rule === 'unlock_on_date' && !this.toIso(m.unlock_at));
      if (undated) {
        this.selectedModuleKey = undated.id || undated._tempId;
        this.flash('Set an unlock date for "' + (undated.course_name || this.courseTitle(undated.course_id)) + '"', 'warn');
        return false;
      }
      this.saving = true;

      try {
        const payload = {
          title: this.pathway.title,
          subtitle: this.pathway.subtitle || null,
          description: this.pathway.description || null,
          thumbnail: this.pathway.thumbnail || null,
          classification: this.pathway.classification || null,
          // Always state the status rather than leaving the API to keep
          // whatever was there. Publishing publishes; saving otherwise holds
          // the status it already has, so editing a live pathway — adding or
          // removing a course while learners hold it — does not quietly
          // unpublish it and empty their dashboards. Taking one down is
          // Archive, deliberately.
          status: publish ? 'published' : (this.pathway.status === 'published' ? 'published' : 'draft'),
        };

        let pathwayId = this.pathway.id;
        if (pathwayId) {
          const r = await this.$http.put('safedig/pathways/' + pathwayId, payload);
          this.pathway.status = (r.data && r.data.status) || this.pathway.status;
        } else {
          const r = await this.$http.post('safedig/pathways', payload);
          pathwayId = r.data.id;
          this.pathway.id = pathwayId;
          this.pathway.status = r.data.status || 'draft';
        }

        // Before the writes: a course taken out and put back would otherwise
        // clash with the row still holding it — one module per course.
        const removed = this.removedModuleIds.slice();
        for (const moduleId of removed) {
          try {
            await this.$http.delete('safedig/pathways/' + pathwayId + '/modules/' + moduleId);
          } catch (e) {
            // Already gone is the state we wanted; anything else is a failure.
            if (!e || !e.response || e.response.status !== 404) throw e;
          }
          this.removedModuleIds = this.removedModuleIds.filter(id => id !== moduleId);
        }

        for (let i = 0; i < this.modules.length; i++) {
          const m = this.modules[i];
          if (!m.id) {
            const r = await this.$http.post('safedig/pathways/' + pathwayId + '/modules', {
              course_id: m.course_id,
              sequence_order: i + 1,
              gating_rule: m.gating_rule,
              gating_config: this.gatingConfig(m),
            });
            m.id = r.data.id;
            m.course_name = m.course_name
              || (r.data.course && r.data.course.name)
              || this.courseTitle(m.course_id);
            // Keep the selection across the temp-id → real-id swap.
            if (this.selectedModuleKey === m._tempId) this.selectedModuleKey = m.id;
            delete m._tempId;
          } else {
            await this.$http.put('safedig/pathways/' + pathwayId + '/modules/' + m.id, {
              gating_rule: m.gating_rule,
              gating_config: this.gatingConfig(m),
            });
          }
        }

        if (this.modules.length > 0) {
          await this.$http.post('safedig/pathways/' + pathwayId + '/modules/reorder', {
            order: this.modules.map((m, i) => ({ id: m.id, sequence_order: i + 1 })),
          });
        }

        if (publish) this.pathway.status = 'published';
        this.dirty = false;
        this.savedAt = Date.now();
        this.flash(
          publish ? 'Pathway published — visible to learners'
            : (this.pathway.status === 'published' ? 'Changes saved — learners see them now' : 'Draft saved'),
          'ok'
        );

        if (!this.$route.params.id && this.pathway.id) {
          this.$router.replace('/safedig/pathways/' + this.pathway.id + '/edit');
        }
        return true;
      } catch (e) {
        const msg = (e && e.response && e.response.data && e.response.data.message) || 'Save failed';
        this.flash(msg, 'error');
        return false;
      } finally {
        this.saving = false;
      }
    },

    flash(msg, kind) {
      this.toast = { msg, kind: kind || 'ok' };
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => { this.toast = null; }, 2400);
    },
    goBack() { this.$router.push('/safedig/pathways'); },
  },
};
</script>

<style scoped>
.sd-pathway-builder-wrap { width: 100%; }
.sd-pathway-builder-wrap >>> .card.no-border-card {
  border: 0;
  box-shadow: none;
  background: transparent;
}

.pb {
  --sd-accent: #3e7799;
  --sd-accent-dark: #2d5a73;
  --sd-accent-soft: rgba(62, 119, 153, 0.14);
  --sd-navy: #1e3a5f;
  --sd-text: #0f172a;
  --sd-muted: #475569;
  --sd-border: #e2e8f0;
  --sd-radius: 12px;
  --sd-shadow: 0 1px 2px rgba(15, 23, 42, 0.06), 0 6px 16px rgba(15, 23, 42, 0.05);

  position: relative;
  padding: 1.5rem 1.75rem 2rem;
  color: var(--sd-text);
  background: #f8fafc;
  min-height: 100%;
}

/* Header ──────────────────────────────────────────────────── */
.pb-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
}
.pb-top__left { flex: 1; min-width: 260px; }
.pb-crumbs {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8125rem;
  margin-bottom: 0.5rem;
}
.pb-crumb { color: var(--sd-accent); cursor: pointer; font-weight: 600; }
.pb-crumb--current { color: var(--sd-muted); cursor: default; font-weight: 400; }
.pb-crumb-sep { color: #cbd5e1; }

.pb-title-row { display: flex; align-items: center; gap: 0.6rem; }
.pb-title-input {
  flex: 1;
  min-width: 0;
  border: 1px solid transparent;
  background: transparent;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--sd-navy);
  padding: 0.2rem 0.4rem;
  border-radius: 8px;
  outline: none;
}
.pb-title-input:hover { border-color: var(--sd-border); }
.pb-title-input:focus { border-color: var(--sd-accent); background: #fff; }
.pb-subtitle-input {
  width: 100%;
  border: 1px solid transparent;
  background: transparent;
  font-size: 0.875rem;
  color: var(--sd-muted);
  padding: 0.25rem 0.4rem;
  border-radius: 8px;
  outline: none;
  margin-top: 0.15rem;
}
.pb-subtitle-input:hover { border-color: var(--sd-border); }
.pb-subtitle-input:focus { border-color: var(--sd-accent); background: #fff; }

.pb-status {
  display: inline-block;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}
.pb-status--draft     { background: #fef3c7; color: #b45309; }
.pb-status--published { background: #dcfce7; color: #15803d; }
.pb-status--archived  { background: #f1f5f9; color: var(--sd-muted); }

.pb-meta-row { margin-top: 0.6rem; display: flex; align-items: flex-start; gap: 1.25rem; flex-wrap: wrap; }
/* align-items: flex-start matters: the chevron is positioned against the
   select wrap, and a stretched wrap (the flex-column default) grows to the
   width of the hint text below it, carrying the chevron out of the select. */
.pb-meta-field { display: flex; flex-direction: column; align-items: flex-start; gap: 0.25rem; }
.pb-meta-field .pb-meta-select { min-width: 220px; }
.pb-meta-label { font-size: 0.8125rem; font-weight: 600; color: var(--sd-muted); margin: 0; }
.pb-meta-required { color: #b91c1c; }
.pb-meta-select--missing { border-color: #fca5a5; }
.pb-meta-hint { font-size: 0.7rem; color: var(--sd-muted); max-width: 34ch; }
.pb-meta-hint--warn { color: #b45309; }
.pb-meta-select-wrap { position: relative; display: inline-block; }
.pb-meta-select {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid var(--sd-border);
  border-radius: 8px;
  background: #fff;
  font-size: 0.8125rem;
  color: var(--sd-text);
  padding: 0.4rem 1.7rem 0.4rem 0.65rem;
  cursor: pointer;
  outline: none;
}
.pb-meta-select__chev {
  position: absolute;
  right: 0.55rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--sd-muted);
  pointer-events: none;
}

.pb-top__stats { display: flex; gap: 1.5rem; }
.pb-stat { text-align: center; }
.pb-stat__value { font-size: 1.35rem; font-weight: 700; color: var(--sd-navy); line-height: 1.1; }
.pb-stat__value--muted { color: #cbd5e1; }
.pb-stat__unit { font-size: 0.75rem; font-weight: 600; color: var(--sd-muted); margin-left: 0.1rem; }
.pb-stat__label { font-size: 0.7rem; color: var(--sd-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-top: 0.15rem; }

.pb-top__actions { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.pb-saved { font-size: 0.775rem; display: inline-flex; align-items: center; gap: 0.25rem; }
.pb-saved--dirty { color: #b45309; }
.pb-saved--ok { color: #15803d; }

.pb-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.95rem;
  border-radius: 9px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--sd-border);
  background: #fff;
  color: var(--sd-text);
  line-height: 1;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.pb-btn:hover:not(:disabled) { border-color: var(--sd-accent); color: var(--sd-accent); }
.pb-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pb-btn--primary { background: var(--sd-accent); border-color: var(--sd-accent); color: #fff; }
.pb-btn--primary:hover:not(:disabled) { background: var(--sd-accent-dark); border-color: var(--sd-accent-dark); color: #fff; }
.pb-btn--icon { padding: 0.55rem 0.6rem; }
.pb-btn--block { width: 100%; justify-content: center; }
.pb-btn--destructive { color: #b91c1c; border-color: #fecaca; background: #fef2f2; }
.pb-btn--destructive:hover:not(:disabled) { background: #fee2e2; border-color: #fca5a5; color: #991b1b; }

.pb-kebab-wrap { position: relative; }
.pb-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 30;
  background: #fff;
  border: 1px solid var(--sd-border);
  border-radius: 10px;
  box-shadow: var(--sd-shadow);
  min-width: 190px;
  padding: 0.3rem;
}
.pb-menu__item {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 0.5rem 0.6rem;
  font-size: 0.8125rem;
  color: var(--sd-text);
  border-radius: 7px;
  cursor: pointer;
}
.pb-menu__item:hover:not(:disabled) { background: #f1f5f9; }
.pb-menu__item:disabled { opacity: 0.45; cursor: not-allowed; }
.pb-menu__item--danger { color: #b91c1c; }
.pb-menu__sep { height: 1px; background: var(--sd-border); margin: 0.25rem 0; }

/* Body layout ─────────────────────────────────────────────── */
.pb-body {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr) 300px;
  gap: 1rem;
  align-items: start;
}
.pb-pane {
  background: #fff;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius);
  box-shadow: var(--sd-shadow);
  overflow: hidden;
}
.pb-pane__head { padding: 0.9rem 1rem; border-bottom: 1px solid var(--sd-border); }
.pb-pane__head--insp { display: flex; align-items: center; justify-content: space-between; }
.pb-pane__title { font-size: 0.9375rem; font-weight: 700; color: var(--sd-navy); margin: 0; }
.pb-pane__sub { font-size: 0.75rem; color: var(--sd-muted); margin: 0.2rem 0 0; }
.pb-pane__loading, .pb-pane__empty {
  padding: 2rem 1rem;
  text-align: center;
  color: var(--sd-muted);
  font-size: 0.8125rem;
}
.pb-spinner {
  display: inline-block;
  width: 22px;
  height: 22px;
  border: 3px solid var(--sd-border);
  border-top-color: var(--sd-accent);
  border-radius: 50%;
  animation: pb-spin 0.8s linear infinite;
}
@keyframes pb-spin { to { transform: rotate(360deg); } }

/* Library ─────────────────────────────────────────────────── */
.pb-search-row { padding: 0.75rem 0.85rem 0.5rem; }
.pb-search { position: relative; }
.pb-search__icon {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--sd-muted);
  pointer-events: none;
}
.pb-search__input {
  width: 100%;
  padding: 0.5rem 0.7rem 0.5rem 1.9rem;
  border: 1px solid var(--sd-border);
  border-radius: 8px;
  font-size: 0.8125rem;
  color: var(--sd-text);
  outline: none;
}
.pb-search__input:focus { border-color: var(--sd-accent); box-shadow: 0 0 0 3px var(--sd-accent-soft); }

.pb-tabs { display: flex; gap: 0.25rem; padding: 0 0.85rem 0.6rem; }
.pb-tab {
  border: 0;
  background: transparent;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--sd-muted);
  padding: 0.3rem 0.5rem;
  border-radius: 7px;
  cursor: pointer;
}
.pb-tab--active { background: var(--sd-accent-soft); color: var(--sd-accent); }

.pb-lib-list {
  list-style: none;
  margin: 0;
  padding: 0 0.6rem 0.75rem;
  max-height: 540px;
  overflow-y: auto;
  /* Keeps the list a real drop area even when it holds nothing. */
  min-height: 140px;
  border-radius: 10px;
  transition: box-shadow 0.15s, background-color 0.15s;
}
.pb-lib-list--removing { box-shadow: 0 0 0 2px #fca5a5 inset; background: #fef2f2; }
/*
 * While a module is held over the library, Sortable parks the canvas's own
 * row in this list to show where it would land. There is nowhere for it to
 * land — the drop removes it — and a canvas tile rendered among the course
 * items reads as a stray module and pushes the whole list about. Give it no
 * room: the red banner already says what the drop will do.
 */
.pb-lib-list > .pb-mod-row {
  height: 0;
  margin: 0;
  padding: 0;
  border: 0;
  overflow: hidden;
  pointer-events: none;
}
.pb-lib-static {
  list-style: none;
  padding: 2rem 1rem;
  text-align: center;
  color: var(--sd-muted);
  font-size: 0.8125rem;
}
.pb-lib-list--removing .pb-lib-static { color: #b91c1c; }
.pb-pane--library { position: relative; }
.pb-remove-hint {
  position: absolute;
  top: 0.5rem;
  left: 0.6rem;
  right: 0.6rem;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.4rem 0.6rem;
  border-radius: 8px;
  background: #b91c1c;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  pointer-events: none;
}
.pb-lib-item__grip { color: #cbd5e1; flex-shrink: 0; display: inline-flex; }
.pb-lib-item:hover .pb-lib-item__grip { color: var(--sd-accent); }
.pb-lib-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.6rem;
  border: 1px solid var(--sd-border);
  border-radius: 9px;
  margin-bottom: 0.4rem;
  background: #fff;
  cursor: grab;
}
.pb-lib-item:hover { border-color: var(--sd-accent); }
.pb-lib-item--used { opacity: 0.6; }
.pb-lib-item__icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: var(--sd-accent-soft);
  color: var(--sd-accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pb-lib-item__body { flex: 1; min-width: 0; }
.pb-lib-item__title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--sd-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pb-lib-item__meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.7rem;
  color: var(--sd-muted);
  margin-top: 0.1rem;
}
.pb-lib-item__time { display: inline-flex; align-items: center; gap: 0.2rem; }
.pb-lib-item__used { color: var(--sd-accent); font-weight: 600; }
.pb-lib-item__add {
  border: 1px solid var(--sd-border);
  background: #fff;
  border-radius: 7px;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--sd-accent);
  cursor: pointer;
  flex-shrink: 0;
}
.pb-lib-item__add:disabled { opacity: 0.4; cursor: not-allowed; }

/* Canvas ──────────────────────────────────────────────────── */
.pb-canvas__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid var(--sd-border);
}
.pb-canvas__summary { font-size: 0.775rem; color: var(--sd-muted); margin-left: 0.5rem; }
.pb-canvas__head-left { display: flex; align-items: baseline; }
.pb-canvas__summary-sep { margin: 0 0.3rem; }
.pb-icon-btn {
  border: 1px solid var(--sd-border);
  background: #fff;
  border-radius: 8px;
  padding: 0.35rem 0.45rem;
  color: var(--sd-muted);
  cursor: pointer;
  line-height: 1;
}
.pb-icon-btn:hover:not(:disabled) { border-color: var(--sd-accent); color: var(--sd-accent); }
.pb-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.pb-icon-btn--danger:hover:not(:disabled) { border-color: #fca5a5; color: #b91c1c; }

.pb-canvas__body { padding: 1rem; min-height: 320px; }
.pb-canvas-list {
  list-style: none;
  margin: 0;
  padding: 0;
  /* Fill the canvas body so an empty list is still a real drop area. */
  min-height: 288px;
  border-radius: 10px;
  transition: box-shadow 0.15s, background-color 0.15s;
}
.pb-canvas-list--receiving { box-shadow: 0 0 0 2px var(--sd-accent) inset; background: #f0f7fb; }
.pb-canvas-list--receiving .pb-empty-state__title,
.pb-canvas-list--receiving .pb-dropzone__title { color: var(--sd-accent); }
.pb-mod-row { list-style: none; }
.pb-canvas-static { list-style: none; }
.pb-mod {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  border: 1px solid var(--sd-border);
  border-radius: 10px;
  background: #fff;
  padding: 0.7rem 0.85rem;
  /* The whole tile is the drag source, so it advertises itself as one. */
  cursor: grab;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.pb-mod:active { cursor: grabbing; }
.pb-mod:hover { border-color: var(--sd-accent); }
.pb-mod:hover .pb-mod__drag { color: var(--sd-accent); }
.pb-mod--selected {
  border-color: var(--sd-accent);
  box-shadow: 0 0 0 3px var(--sd-accent-soft);
}
/* Controls on the tile opt out of the drag and keep their own cursor. */
.pb-mod .pb-rule-select, .pb-mod .pb-mod__remove { cursor: pointer; }
.pb-mod__drag { color: #cbd5e1; flex-shrink: 0; }
.pb-mod__seq {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--sd-accent-soft);
  color: var(--sd-accent);
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pb-mod__main { flex: 1; min-width: 0; }
.pb-mod__title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--sd-navy);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pb-mod__meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.7rem;
  color: var(--sd-muted);
  margin-top: 0.2rem;
}
.pb-mod__time { display: inline-flex; align-items: center; gap: 0.2rem; }
.pb-mod__chip {
  border-radius: 999px;
  padding: 0.1rem 0.45rem;
  font-weight: 600;
}
.pb-mod__chip--sequential { background: var(--sd-accent-soft); color: var(--sd-accent); }
.pb-mod__chip--optional   { background: #f1f5f9; color: var(--sd-muted); }
.pb-mod__chip--date       { background: #fef3c7; color: #b45309; }
.pb-rule-select {
  border: 1px solid var(--sd-border);
  border-radius: 8px;
  font-size: 0.75rem;
  padding: 0.35rem 0.5rem;
  color: var(--sd-text);
  background: #fff;
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
}
.pb-mod__remove {
  border: 0;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  padding: 0.25rem;
  flex-shrink: 0;
}
.pb-mod__remove:hover { color: #b91c1c; }

.pb-flow-sep {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0;
  justify-content: center;
}
.pb-flow-line { flex: 1; height: 1px; background: var(--sd-border); }
.pb-flow-plus {
  border: 1px dashed var(--sd-border);
  background: #fff;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--sd-muted);
  cursor: pointer;
  flex-shrink: 0;
}
.pb-flow-plus:hover { border-color: var(--sd-accent); color: var(--sd-accent); }

.pb-empty-state { text-align: center; padding: 1.5rem 1rem 2rem; }
.pb-empty-state__art { margin-bottom: 0.75rem; }
.pb-empty-state__art svg { max-width: 220px; height: auto; }
.pb-empty-state__title { font-size: 0.9375rem; font-weight: 700; color: var(--sd-navy); }
.pb-empty-state__sub { font-size: 0.8125rem; color: var(--sd-muted); margin: 0.25rem 0 0.9rem; }

.pb-dropzone {
  margin-top: 0.5rem;
  border: 1px dashed var(--sd-border);
  border-radius: 10px;
  padding: 1.1rem;
  text-align: center;
  background: #f8fafc;
}
.pb-canvas-list--receiving .pb-dropzone { border-color: var(--sd-accent); }
.pb-dropzone__plus { color: var(--sd-accent); }
.pb-dropzone__title { font-size: 0.8125rem; font-weight: 600; color: var(--sd-text); margin-top: 0.2rem; }
.pb-dropzone__sub { font-size: 0.75rem; color: var(--sd-muted); margin-top: 0.15rem; }

/* Sortable feedback */
.pb-sortable-ghost { opacity: 0.4; }
.pb-sortable-chosen { box-shadow: 0 0 0 3px var(--sd-accent-soft); }
.pb-sortable-drag { opacity: 0.9; }

/* Inspector ───────────────────────────────────────────────── */
.pb-insp { display: flex; flex-direction: column; }
.pb-insp__scroll { padding: 1rem; max-height: 520px; overflow-y: auto; }
.pb-insp__section { margin-bottom: 1.25rem; }
.pb-insp__course { font-size: 0.875rem; font-weight: 700; color: var(--sd-navy); }
.pb-insp__course-sub { font-size: 0.75rem; color: var(--sd-muted); margin-top: 0.15rem; }
.pb-insp__section-title { font-size: 0.8125rem; font-weight: 700; color: var(--sd-text); }
.pb-insp__section-sub { font-size: 0.75rem; color: var(--sd-muted); margin: 0.15rem 0 0.65rem; }
.pb-radio-group { display: flex; flex-direction: column; gap: 0.5rem; }
.pb-radio {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  border: 1px solid var(--sd-border);
  border-radius: 9px;
  padding: 0.6rem 0.7rem;
  cursor: pointer;
  margin: 0;
}
.pb-radio:hover { border-color: var(--sd-accent); }
.pb-radio input { margin-top: 0.2rem; accent-color: #3e7799; }
.pb-radio__body { display: flex; flex-direction: column; }
.pb-radio__title { font-size: 0.8125rem; font-weight: 600; color: var(--sd-text); }
.pb-radio__sub { font-size: 0.7rem; color: var(--sd-muted); margin-top: 0.1rem; }
.pb-date-field { margin-top: 0.75rem; display: flex; flex-direction: column; gap: 0.3rem; }
.pb-field-label { font-size: 0.8125rem; font-weight: 600; color: var(--sd-text); margin: 0; }
.pb-date-input {
  width: 100%;
  border: 1px solid var(--sd-border);
  border-radius: 8px;
  padding: 0.5rem 0.65rem;
  font-size: 0.8125rem;
  color: var(--sd-text);
  background: #fff;
  outline: none;
}
.pb-date-input:focus { border-color: var(--sd-accent); box-shadow: 0 0 0 3px var(--sd-accent-soft); }
.pb-date-input--missing { border-color: #fca5a5; }
.pb-field-hint { font-size: 0.7rem; color: var(--sd-muted); }
.pb-field-hint--warn { color: #b45309; }
.pb-insp__footer { padding: 0.85rem 1rem; border-top: 1px solid var(--sd-border); }
.pb-insp-close { border: 0; background: transparent; color: var(--sd-muted); cursor: pointer; padding: 0.2rem; }
.pb-insp-empty { padding: 2rem 1rem; text-align: center; }
.pb-insp-empty__title { font-size: 0.875rem; font-weight: 600; color: var(--sd-text); }
.pb-insp-empty__sub { font-size: 0.75rem; color: var(--sd-muted); margin-top: 0.25rem; }

/* Toast ───────────────────────────────────────────────────── */
/* Bottom-centred rather than bottom-right: the Elfsight language selector sits
   in that corner and covered the message. */
.pb-toast {
  position: fixed;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1100;
  max-width: min(90vw, 420px);
  text-align: center;
  padding: 0.85rem 1.4rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.28);
}
.pb-leave {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.45);
}
.pb-leave__box {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 14px;
  padding: 1.25rem 1.35rem;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.25);
}
.pb-leave__title { font-size: 1.05rem; font-weight: 700; color: var(--sd-navy); margin: 0 0 0.4rem; }
.pb-leave__text { font-size: 0.875rem; color: var(--sd-muted); margin: 0 0 1.1rem; }
.pb-leave__actions { display: flex; gap: 0.5rem; justify-content: flex-end; flex-wrap: wrap; }

.pb-toast--ok    { background: #15803d; }
.pb-toast--warn  { background: #b45309; }
.pb-toast--error { background: #b91c1c; }

@media (max-width: 1200px) {
  .pb-body { grid-template-columns: 240px minmax(0, 1fr); }
  .pb-pane--inspector { grid-column: 1 / -1; }
}
@media (max-width: 820px) {
  .pb-body { grid-template-columns: minmax(0, 1fr); }
  .pb { padding: 1.25rem 1rem 2rem; }
}
</style>
